/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/table.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import './styles.scss'
import './disco-styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { Link, ChapterNavigation } from '@ta-interaktiv/contentbricks'
import { ArticleTeasers } from '@ta-interaktiv/react-article-teasers'
import Cover from '../cover'

const ARTICLE_ID = '367341950277'
const isMobile = window.innerWidth < 600
const navigationData = [
  {
    name: 'Einleitung',
    id: '#einleitung'
  },
  {
    name: 'Haushalt/Familie',
    id: '#haushalt'
  },
  {
    name: 'Restaurants',
    id: '#restaurant'
  },
  {
    name: 'Grossraumbüro',
    id: '#buero'
  },
  {
    name: 'Bus/öffentlicher Verkehr',
    id: '#verkehr'
  },
  {
    name: 'Chor',
    id: '#chor'
  },
  {
    name: 'Gottesdienste/religiöse Feste',
    id: '#gottesdienst'
  },
  {
    name: 'Bars/Clubs',
    id: '#nachtclub'
  },
  {
    name: 'Spitäler',
    id: '#spital'
  },
  {
    name: 'Fitness',
    id: '#fitness'
  },
  {
    name: 'Beerdigungen, Hochzeiten, Geburtstage, Familienfeste',
    id: '#beerdigung'
  },
  {
    name: 'Schlachthöfe und Fleischverpackungen',
    id: '#schlachthof'
  },
  {
    name: 'Über die Rolle der Superspreader',
    id: '#superspreader'
  }
]

export function AppComponent() {
  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />

      <header className='uivertical fitted segment'>
        <Cover
          title='Wo die Gefahr hoch ist, sich mit dem Coronavirus anzustecken'
          subtitle='Ab dem 6. Juni sind in der Schweiz viele Orte mit grossem
          epidemiologischen Verbreitungspotenzial wieder zugänglich. Wir
          zeigen, wo die Ansteckungsgefahr mit Sars-CoV-2 besonders gross
          ist.'
        />
      </header>

      <div className='ui vertical segment'>
        <div className='ui text container'>
          <div className='ta metainfo'>
            <div className='ta author'>
              <nobr>Marc Brupbacher</nobr> und <nobr>Mathias Lutz</nobr>
            </div>
            <div className='ta updated'>Aktualisiert am 6. Juni 2020</div>
          </div>
        </div>
        <div className='ui text container'>
          <ChapterNavigation content={navigationData} />
        </div>
        <div
          id='einleitung'
          className='ui text container'
          style={{ margin: '2em 0' }}
        >
          <p>
            Die Anzeichen verdichten sich, dass sich das neue Coronavirus nicht
            nur durch Niesen, Husten und feuchtes Sprechen, die
            Tröpfcheninfektion, verbreiten kann, sondern auch in Form eines
            Aerosols. Dabei handelt es sich um besonders feinen virushaltigen
            Nebel, der schon beim normalen Atmen und Sprechen (und erst recht
            beim Singen) entsteht und lange in der Luft stehen bleibt. Der
            Berliner Virologe Christian Drosten schätzt im{' '}
            <Link href='https://www.ndr.de/nachrichten/info/40-Jetzt-ist-Alltagsverstand-gefragt,audio681130.html'>
              NDR-Podcast
            </Link>
            , dass fast die Hälfte der Virusübertragungen auf Aerosole
            zurückzuführen sind, die andere Hälfte durch grössere Tröpfchen und
            nur rund zehn Prozent durch Schmier- oder Kontaktinfektionen.
          </p>
          <p>
            Eine{' '}
            <Link href='https://www.pnas.org/content/early/2020/05/12/2006874117'>
              Studie
            </Link>{' '}
            stützt diese These. US-Forscher vom National Institute of Diabetes
            and Digestive and Kidney Diseases (NIDDK) haben untersucht, wie
            viele Aerosole durch normales Sprechen ausgestossen werden. Dafür
            mussten die Personen 25 Sekunden lang laut den Satz «Stay healthy!»
            («Bleib gesund!») wiederholen. Ausgesucht wurde der Satz wegen des
            «th», das im Englischen besonders viele Tröpfchen produziert. Um
            diese messbar zu machen, projizierte das Forscherteam einen Laser in
            den Raum.
          </p>
          <p>
            <Link hideIcon href='https://www.youtube.com/watch?v=axmRl6P6xyw'>
              <img
                className='ui fluid image'
                src={require('./images/stay-healthy-niddk.gif')}
              />
            </Link>
            <div className='source'>
              Quelle und vollständiges Video (1:06 Minuten):{' '}
              <Link href='https://www.youtube.com/watch?v=axmRl6P6xyw'>
                NIDDK
              </Link>
            </div>
          </p>
          <p>
            Das Ergebnis: Eine Person stiess in jeder Minute mehr als tausend
            virusbelastete Tröpfchen aus. Diese schweben in einem geschlossenen
            Raum 8 bis 14 Minuten lang in der Luft. Demnach ist laut den
            US-Forschern ein Beleg erbracht, dass nicht nur Husten und Niesen,
            sondern auch Sprechen eine ansteckende Aerosolwolke produzieren kann
            – je lauter gesprochen wird, desto mehr Tröpfchen werden
            ausgestossen. Dieser Befund könnte erklären, wie Menschen mit
            leichten oder keinen Symptomen andere Personen in engen Räumen wie
            Büros, Musikclubs, Pflegeheimen und Kreuzfahrtschiffen in so grosser
            Zahl infizieren konnten. Eine Ansteckung wird umso wahrscheinlicher,
            je kleiner ein Raum und je länger die Sprechaktivität ist. Im Fazit
            heisst es, «dass es eine erhebliche Wahrscheinlichkeit gibt, dass
            normales Sprechen in beschränkten Umgebungen eine Übertragung von
            Viren in der Luft verursacht».
          </p>
          <p>
            Ein japanisches Experiment zeigte, dass Aerosole bis 20 Minuten in
            der Luft schweben. In diesem{' '}
            <Link href='https://www.youtube.com/watch?v=vBvFkQizTT4&feature=youtu.be'>
              Video
            </Link>
            , welches die Studie dokumentiert, ist am Schluss auch zu sehen, was
            für eine Wirkung ein geöffnetes Fenster haben kann: Die
            Schwebeteilchen werden innert kürzester Zeit hinausgetragen.
          </p>
          <p>
            <Link
              hideIcon
              href='https://www.youtube.com/watch?v=vBvFkQizTT4&feature=youtu.be'
            >
              <img
                className='ui fluid image'
                src={require('./images/aerosole-nhk-worldreport.gif')}
              />
            </Link>
            <div className='source'>
              Quelle und vollständiges Video (6:40 Minuten):{' '}
              <Link href='https://www.youtube.com/watch?v=vBvFkQizTT4&feature=youtu.be'>
                NHK World Report
              </Link>
            </div>
          </p>
          <p>
            Der Blick auf den feinen Atemnebel ist bei Sars-CoV-2 relativ neu.
            Anfänglich waren Fachleute davon ausgegangen, dass sich Sars-CoV-2
            vor allem durch Tröpfcheninfektion und in seltenen Fällen auch durch
            Schmierinfektion, also das Anfassen von benetzten Handgriffen oder
            Gegenständen, verbreitet. Doch es kamen immer mehr Belege für
            Aerosol-Übertragungen hinzu. So hatten Wissenschaftler von der
            University of Montana schon Mitte März im{' '}
            <Link href='https://www.nejm.org/doi/full/10.1056/NEJMc2004973'>
              New England Journal of Medicine
            </Link>{' '}
            berichtet, dass Sars-CoV-2 lange in winzigen Tröpfchen überdauert.
            Sie fertigten ein Aerosol mithilfe einer Maschine aus einer stark
            mit Viren verseuchten Flüssigkeit an. Die Partikel hielten sich drei
            Stunden lang und blieben ebenso lange infektiös.
          </p>
          <p>
            Wenn sich die Übertragung durch Aerosole bestätigt, hat das
            Konsequenzen für den Schutz vor Corona. Denn Abstand hilft dann auch
            nicht mehr viel. Zwei Meter Distanz halten ist weiterhin gut gegen
            die Tröpfchen. Gegen Aerosole helfen frische Luft und Luftbewegung.
            Denn jeder Luftzug vertreibt das Virus und verdünnt den Nebel. Die
            nötige Konsequenz: Lüften, Ventilatoren –- und das Leben nach
            draussen verlagern.
          </p>
          <p>
            Was Forscher noch nicht wissen, ist, ob alle Tröpfchen, die
            Viruspartikel tragen, mit Sprache, Husten und Niesen gleichermassen
            infektiös sind und wie viel an Virus übertragen werden muss, damit
            eine Person durch Einatmen krank wird.
          </p>
          <p>
            Einige{' '}
            <Link href='https://www.sciencemediacentre.org/expert-reaction-to-questions-about-covid-19-and-viral-load/'>
              Experten
            </Link>{' '}
            schätzen, dass schon 1000 infektiöse Sars-CoV-2-Viruspartikel
            ausreichen, um jemanden zu infizieren. Auf Basis dieser Zahl
            berechnete Erin Bromage, Biologieprofessor an der Universität von
            Massachusetts, in einem viel beachteten{' '}
            <Link href='https://www.erinbromage.com/post/the-risks-know-them-avoid-them'>
              Beitrag
            </Link>
            , wie diese Menge über die Schleimhäute oder Augen aufgenommen
            werden kann, um jemanden krank machen.
          </p>
          <p>
            <strong>Husten und Niesen:</strong> Einmal <strong>Husten</strong>{' '}
            setzt etwa 3000 Tröpfchen frei, die meisten sind gross und fallen
            schnell auf den Boden, aber viele bleiben in der Luft und können
            sich in wenigen Sekunden durch einen Raum bewegen. Einmal{' '}
            <strong>Niesen</strong> setzt etwa 30’000 Tröpfchen frei, die
            meisten sind klein und bewegen sich leicht durch einen Raum. Wenn
            eine Person infiziert ist, können die Tröpfchen bei nur einmal
            Husten oder Niesen bis zu 200 Millionen Viruspartikel enthalten, die
            sich alle in der Umgebung verteilen können.
          </p>
          <p>
            <strong>Atmen und Sprechen:</strong> Ein einziger Atemzug setzt 50
            bis 5000 Tröpfchen frei – Sprechen erhöht die Freisetzung um das
            Zehnfache. Die meisten dieser Tröpfchen haben eine geringe
            Geschwindigkeit und fallen schnell auf den Boden. Die durch die
            Atmung freigesetzten Atmungströpfchen enthalten nur geringe
            Virusmengen. Bei der Grippe ist es so: Eine infizierte Person kann
            bis zu 33 infektiöse Viruspartikel pro Minute freisetzen, beim
            Sprechen wären es maximal 330.
          </p>
          <p>
            <div style={{ display: 'block', marginBottom: '0.4em' }}>
              Bromage zieht daraus folgende Schlussfolgerung:
            </div>
            <strong>
              «Wenn eine Person hustet oder niest, gelangen 200 Millionen
              Viruspartikel überall hin. Manche Viren hängen einige Minuten in
              der Luft, die meisten fallen auf den Boden. Wenn eine Person eine
              andere anhustet, ist es relativ einfach, 1000 Viruspartikel
              einzufangen. Aber selbst wenn man einen Raum betritt, in welchem
              vorher jemand geniest oder gehustet hat, ist es relativ leicht,
              durch ein paar Atemzüge diese Menge an Partikeln einzufangen.
              Niesen und husten stossen so viele Viren aus, dass ein ganzer Raum
              mit Menschen infiziert werden könnte. In einem Gespräch Angesicht
              zu Angesicht bräuchte es für die Dosis der 1000 Viruspartikel
              ungefähr 5 Minuten.»
            </strong>
          </p>
          <p>
            Mit diesem Wissen schauen wir nun einzelne Situationen etwas näher
            an, in welchen man besonders vor Sars-CoV-2 aufpassen muss. Jede
            geschlossene Umgebung mit schlechter Luftzirkulation und hoher
            Menschendichte, die lange beieinander ist, bedeutet Gefahr
          </p>
        </div>

        <div id='haushalt' className='ui text location container'>
          <h3>Haushalt/Familie</h3>
          <img
            className='ui fluid image illu'
            src={require('./images/haushalt.svg')}
          />
          <p>
            Wenig überraschend passierten viele Ansteckungen im Kreis der
            eigenen Familie. In einer österreichischen
            <Link href='https://www.ages.at/service/service-presse/pressemeldungen/epidemiologische-abklaerung-am-beispiel-covid-19/'>
              Kontaktverfolgungs-Untersuchung
            </Link>{' '}
            konnten 7,3 Prozent aller Ansteckungen direkt auf die eigene Familie
            zurückgeführt werden. Das Gesundheitsdepartement des Kantons
            Basel-Stadt hat am 24. April 2020{' '}
            <Link href='https://www.bzbasel.ch/basel/basel-stadt/details-zur-ausbreitung-des-coronavirus-die-meisten-basler-steckten-sich-im-familienkreis-an-137717318'>
              Details zur Ausbreitung des Coronavirus
            </Link>{' '}
            im Kanton veröffentlicht. Die allermeisten (19%) haben sich im
            familiären Umfeld mit Sars-CoV-2 infiziert. Danach folgen
            Ansteckungen in Institutionen wie Spitälern (12%) und bei der Arbeit
            (10%). Der Kantonsärztliche Dienst des Kantons Aargau schreibt am
            26. Mai: «Als mögliche Infektionsorte wurden im Wesentlichen der
            Arbeitsplatz, die Familie sowie die Nachbarschaft angegeben.»{' '}
            <Link href='http://html.rhhz.net/zhlxbx/028.htm'>
              Eine chinesische Studie
            </Link>{' '}
            kam zum Schluss, dass sich im Durchschnitt ungefähr ein
            Familienmitglied von fünf ansteckt, wenn jemand im Haushalt
            erkrankt. Zwei andere Studien (
            <Link href='https://academic.oup.com/cid/advance-article/doi/10.1093/cid/ciaa450/5821281'>
              hier
            </Link>{' '}
            und{' '}
            <Link href='https://www.medrxiv.org/content/10.1101/2020.04.11.20056010v1'>
              hier
            </Link>
            ) kommen zu ähnlichen Ergebnissen. Demnach liegt das Risiko, sich
            bei einer infizierten Person im Haushalt anzustecken, im Schnitt bei
            16 oder 17 Prozent. Der Partner hat ein besonders hohes Risiko: Im
            Schnitt infizierten sich drei von zehn Ehepartnern (28 Prozent),
            wenn der andere das Virus in sich trug.
          </p>
        </div>

        <div id='restaurant' className='ui text location container'>
          <h3>Restaurants</h3>
          <img
            className='ui fluid image illu'
            src={require('./images/restaurant.svg')}
          />
          <p>
            Ein mittlerweile berühmtes Beispiel zu Ansteckungen in einem
            Restaurant ereignete sich in der Stadt Guangzhou im Süden von China.
            Eine infizierte Person (A1) sass an einem Tisch und ass mit
            Familienmitgliedern und Freunden. Das Mittagessen dauerte etwa 1 bis
            1,5 Stunden im fensterlosen Raum. Während dieser Mahlzeit setzte die
            ansteckende Person, die keine Symptomer zeigte, über seine Atmung
            und beim Sprechen Virusmengen in die Luft frei. Die Luft wurde von
            der Klimaanlage von rechts nach links geweht. Etwa die Hälfte der
            Personen am Tisch der infizierten Person wurden in den nächsten 7
            Tagen krank. Auch einige Personen an den benachbarten Tischen wurden
            krank. Insgesamt erkrankten 10 Personen aus drei Familien. Von jenen
            Personen, die weiter weg sassen und sich nicht direkt im Luftstrom
            befanden, wurde niemand krank. Die Wissenschaftler gehen davon aus,
            dass die Klimaanlage in diesem Fall eine entscheidende Rolle
            spielte. Durch deren Luftstrom könnten die Viren deutlich weiter
            transportiert worden sein als beim Sprechen, Husten oder Niesen. Die
            amerikanische{' '}
            <Link href='https://wwwnc.cdc.gov/eid/article/26/7/20-0764_article'>
              Seuchenschutzbehörde CDC
            </Link>{' '}
            empfiehlt daher in ihrem detaillierten Bericht zum Fall, im
            Restaurant den Abstand zwischen den Tischen zu vergrössern und die
            Belüftung zu verbessern.
          </p>
          <p>
            <img
              className='ui fluid image'
              src={require('./images/restaurant-guangzhou-cdc.jpg')}
            />
            <div className='caption'>
              Die infizierte Indexperson A1 sass am Tisch A. Alle rot umkreisten
              Personen erkrankten später an Covid-19. Die Klimaanlage vor dem
              Tisch C blies die Luft von rechts nach links.
            </div>
            <div className='source'>
              Quelle:{' '}
              <Link href='https://wwwnc.cdc.gov/eid/article/26/7/20-0764-f1'>
                cdc.gov
              </Link>
            </div>
          </p>
          <p>
            Ein aktuelles Beispiel aus Deutschland zeigt ebenfalls, dass
            Restaurants nicht unproblematisch sind: Nach dem Besuch eines Lokals
            im niedersächsischen Landkreis Leer sind über 35 Menschen positiv
            auf das Coronavirus getestet worden, über 200 mussten in Quarantäne.
            An dem Abend mit geladenen Gästen waren insgesamt mindestens 40
            Menschen im Restaurant. Dem Wirt wird vorgeworfen, dass er bei der
            privaten Feier gegen diverse Corona-Regeln verstossen hat. Es wurden
            Verfahren eingeleitet.
          </p>
          <p>
            Dass die Menschen in Restaurants jetzt wieder dicht nebeneinander
            sitzen, hält der deutsche Virologe Drosten für gefährlich.
            Aussenbereiche seien hingegen relativ sichere Zonen, da dort das
            Aerosol weggeweht werde, statt in der Luft stehen zu bleiben. Wenn
            man bei schönem Wetter die Fenster öffne, sei es auch in
            Innenbereichen sicherer, aber mit den Abstandsregeln.
          </p>
        </div>

        <div id='buero' className='ui text location container'>
          <h3>Grossraumbüro</h3>
          <img
            className='ui fluid image illu'
            src={require('./images/buero.svg')}
          />
          <p>
            Ein weiteres gut dokumentiertes Beispiel ist der Ausbruch in einem
            Callcenter in Südkorea, der von einer einzigen infizierten Person
            ausging. Von 216 Mitarbeitern auf dem Stock erkrankten über eine
            Woche 94 (44%, blaue eingezeichnet). 92 dieser 94 Personen wurden
            krank (nur 2 blieben asymptomatisch) Fast alle Fallpatienten
            befanden sich auf einer Seite des Gebäudes im 11. Stock. Trotz
            erheblicher Interaktion zwischen den Mitarbeitern auf verschiedenen
            Etagen des Gebäudes in den Aufzügen und in der Lobby war die
            Verbreitung von Sars-CoV-2 fast ausschliesslich auf die 11. Etage
            beschränkt, was darauf hinweist, dass die Dauer der Interaktion
            wahrscheinlich die Hauptursache des Ausbruchs ist, wie dieser
            <Link href='https://wwwnc.cdc.gov/eid/article/26/8/20-1274_article'>
              Bericht
            </Link>{' '}
            zeigt. Dieser Ausbruch macht deutlich, wie schnell sich das Virus in
            überfüllten Büroräumlichkeiten ausbreitet, in welchen viel und
            andauernd gesprochen wird. Die Wahrscheinlichkeit einer Infektion
            steigt, wenn man sich in einem geschlossenen Raum aufhält und über
            einen längeren Zeitraum die gleiche Luft teilt.
          </p>
          <p>
            <img
              className='ui fluid image'
              src={require('./images/callcenter-southkorea-cdc.jpg')}
            />
            <div className='caption'>
              An allen Plätzen, die blau markiert sind, sassen Personen, die an
              Covid-19 erkrankten. Vor allem auf einer Seites des Büros, in
              welchem auch die infizierte Indexperson sass, ist die
              Erkrankungsrate sehr hoch.
            </div>
            <div className='source'>
              Quelle:{' '}
              <Link href='https://wwwnc.cdc.gov/eid/article/26/8/20-1274-f2'>
                cdc.gov
              </Link>
            </div>
          </p>
          <hr />
          <p>
            LESEN SIE AUCH
            <br />
            <Link
              href='553237989006'
              icon='icon file alternate outline'
              allowWordWrap
            >
              Wie ansteckend ist die Luft im Büro?
            </Link>
          </p>
        </div>

        <div id='verkehr' className='ui text location container'>
          <h3>Bus/öffentlicher Verkehr</h3>
          <img
            className='ui fluid image illu'
            src={require('./images/verkehr.svg')}
          />
          <p>
            Diese{' '}
            <Link href='https://www.researchgate.net/publication/340418430_Airborne_transmission_of_COVID-19_epidemiologic_evidence_from_two_outbreak_investigations'>
              Studie
            </Link>{' '}
            geht Übertragungswegen in einem Bus in der chinesischen Provinz
            Zhejiang nach. 146 Passagiere nahmen zwei Busse und fuhren 150
            Minuten zu einer religiösen Veranstaltung. Die Infizierte Person (in
            der Grafik als IP bezeichnet) sass in Bus 1, in welchem sich 23 von
            67 Personen ansteckten (34%). Es gab Übertragungen quer durch den
            Bus, nicht nur direkt neben der Indexperson. Die Autoren der Studie
            kommen deshalb ebenfalls zum Schluss, dass Aerosole zumindest
            teilweise eine Rolle für die hohe Ansteckungsraten spielten.
          </p>
          <p>
            <img
              className='ui fluid image'
              src={require('./images/bus-zhejiang-researchgate.jpg')}
            />
            <div className='caption'>
              Die infizierte Indexperson sass im linken Teil des Busses in der
              Mitte (IP). Rundherum erkrankten zahlreiche Personen an Covid-19 -
              die meisten heftig.
            </div>
            <div className='source'>
              Quelle:{' '}
              <Link href='https://www.researchgate.net/figure/Schematic-diagram-of-Bus-2-IP-Index-patient-NC-Non-case-C-Case-number-with-texts_fig1_340418430'>
                researchgate.net
              </Link>
            </div>
          </p>
          <p>
            Zwei Kontakt-Verfolgungs-Studien aus China (
            <Link href='http://html.rhhz.net/zhlxbx/028.htm'>1</Link> und{' '}
            <Link href='https://www.medrxiv.org/content/10.1101/2020.04.04.20053058v1.full.pdf'>
              2
            </Link>
            ) zeigen, dass es in Transportmitteln jeweils am zweitmeisten zu
            Übertragungen kam. Eine andere Studie deutet ebenfalls an, dass die
            Subway in New York City massgeblich an der raschen Verbreitung des
            Virus innerhalb der Stadt beteiligt gewesen sein könnte. Der Ökonom
            Jeffrey Harris vom Massachusetts Institute of Technology (MIT)
            analysierte einerseits die Pendlerströme des New Yorker
            U-Bahn-Systems und anderseits die Zahl der Infektionen. Die
            Pendlerströme in der Subway konnte Harris anhand der
            Drehkreuzbewegungen am Ein- und Ausgang der Stationen exakt
            verfolgen. Den Pendlerströmen stellte er die Infektionszahlen
            gegenüber. Auch diese lassen sich in New York City sehr genau
            lokalisieren. Das Resultat: Entlang jener Strecken, wo die
            Passagierzahlen höher blieben, verbreitete sich auch das Virus
            schneller unter der Bevölkerung. Die österreichische
            Gesundheitsagentur{' '}
            <Link href='https://www.ages.at/startseite/'>AGES</Link> hat die
            Infektionsketten mithilfe lokaler Behörden ebenfalls rekonstruiert.
            Die Ergebnisse weichen von den obigen Studien ab: «Nach Abgleich der
            Infizierten-Gruppen konnte bislang keine einzige Infektionskette
            belegt werden, die auf eine Ansteckung in öffentlichen
            Verkehrsmitteln zurückzuführen ist.» Dies könnte auch damit
            zusammenhängen, dass in den letzten Monaten nur noch wenige Personen
            mit Bus, Tram und Zug fuhren und eine Ansteckung in den
            Verkehrsmittel schwierig zu beweisen ist.
          </p>
        </div>

        <div id='chor' className='ui text location container'>
          <h3>Chor</h3>
          <img
            className='ui fluid image illu'
            src={require('./images/chor.svg')}
          />
          <p>
            Als sich die Mitglieder des Skagit Valley Chorale am 10. März zu
            ihrer Probe trafen, taten sie das keineswegs unvorbereitet. Sie
            wussten, dass Sars-CoV-2 eine potenzielle Gefahr darstellte. Deshalb
            trafen sie Vorsichtsmassnahmen, bevor sie sich im US-Bundesstaat
            Washington zusammenfanden. Desinfektionsmittel standen bereit,
            Umarmungen und Küsschen waren verboten. Doch die Probe entwickelte
            sich trotzdem zum Desaster. Ein Sänger war mit Sars-CoV-2 infiziert
            zur Probe erschienen, am Ende waren 53 der 61 Probenteilnehmer
            krank,
            <Link href='https://www.nytimes.com/2020/05/12/health/coronavirus-choir.html?referringSource=articleShare'>
              Durchschnittsalter 69 Jahre
            </Link>
            . Bei insgesamt 33 Sängern wurde das Coronavirus nachgewiesen, bei
            weiteren 20 geht die US-Seuchenschutzbehörde Centers for Disease
            Control (CDC), die den Fall untersucht hat, in ihrem
            <Link href='https://www.cdc.gov/mmwr/volumes/69/wr/mm6919e6.htm?s_cid=mm6919e6_w&fbclid=IwAR1QI5yQLgUl7O0YVCRUgkjPWSNZTjyG0kjyV_TTBkYg1BuVJZ4KAd-kNKE#contribAff'>
              Abschlussbericht
            </Link>{' '}
            von einer Corona-Infektion aus. Drei Sänger kamen ins Krankenhaus,
            zwei sind gestorben. Die Sänger in Washington hätten zwar zum Teil
            dicht nebeneinander gesessen, und manche hätten auch Kekse und
            Mandarinen geteilt, folgern die CDC. Doch das erklärt nur in Teilen
            die massive Verbreitung des Virus unter den Chormitgliedern. Auch
            der Akt des Singens selbst «könnte durch die Emission von Aerosolen
            beigetragen haben», so die CDC.
          </p>
          <p>
            In einem Amsterdamer Laienchor erkrankten nach einer Aufführung von
            Bachs «Johannes-Passion» im März 102 der 130 Mitglieder an Covid-19;
            ein Sänger und drei Partner von Chormitgliedern starben. Ähnliches
            passierte in der Berliner Domkantorei, in der 60 von 80 Sängern
            erkrankten.{' '}
            <Link href='https://www.evangelisch.de/inhalte/168202/06-04-2020/berliner-domkantorei-quarantaene'>
              Von der Domverwaltung
            </Link>{' '}
            hiess es später: Die lange Verweildauer in einem Raum habe «es
            offensichtlich unerheblich gemacht, wie weit man voneinander weg
            sitzt». Auch im französischen Hombourg-Haut, im bayrischen Hohenberg
            oder im luzernischen Buttisholz steckten sich Chorsänger an.
          </p>
          <p>
            In der Schweiz gibt es ebenfalls einen Vorfall. Ein Chor traf sich
            in einer kleinen Landgemeinde im Kanton Luzern am 10. März zur
            Probe. Die Corona-Krise war bereits in aller Munde, man wusste, dass
            Leute über 65 – also viele Aktive des Kirchenchors – besonders
            gefährdet sind. Die Probe fand trotzdem statt. Womit niemand
            rechnete: Ein Mitglied trug das Virus in sich. Der Chorpräsident
            schildert, was in den folgenden Tagen passierte: Vier Tage nach der
            verhängnisvollen Probe mit rund 30 Anwesenden erfuhr er, dass eine
            Person positiv getestet worden sei. Es kamen immer mehr dazu. Bis
            zum 19. März waren acht Teilnehmer an Corona erkrankt, einige
            weitere zeigten Symptome, liessen sich aber nicht testen.
          </p>
          <p>
            Biologieprofessor Bromage schreibt in{' '}
            <Link href='https://www.erinbromage.com/post/the-risks-know-them-avoid-them'>
              seinem Beitrag
            </Link>
            : «Singen aerosoliert Atemtröpfchen ausserordentlich gut. Tiefes
            Einatmen beim Gesang ermöglicht es den Viren tief in die Lungen zu
            gelangen. Die mehrstündigen Proben sind lange genug, damit die
            meisten Teilnehmer genug Viren ausgesetzt sind, um krank zu werden.»
          </p>
          <hr />
          <p>
            LESEN SIE AUCH
            <br />
            <Link
              href='254161939164'
              icon='icon file alternate outline'
              allowWordWrap
            >
              Wie gefährlich sind Chöre?
            </Link>
            <br />
            <Link
              href='552418763598'
              icon='icon file alternate outline'
              allowWordWrap
            >
              Sind Blasinstrumente Virenschleudern?
            </Link>
          </p>
        </div>

        <div id='gottesdienst' className='ui text location container'>
          <h3>Gottesdienste/religiöse Feste</h3>
          <img
            className='ui fluid image illu'
            src={require('./images/gottesdienst.svg')}
          />
          <p>
            Nach einem Gottesdienst am 10. Mai in Frankfurt haben sich über 200
            Personen mit dem Coronavirus infiziert – trotz Schutzkonzepten. So
            habe man separate Ein- und Ausgänge benutzt, ausreichend
            Desinfektionsmittel bereitgestellt und einen Abstand von 1,5 Meter
            eingehalten. «Im Nachhinein betrachtet wäre es für uns angebracht,
            beim Gottesdienst Mund-Nasen-Schutzbedeckungen zu tragen und auf den
            gemeinsamen Gesang zu verzichten», schreiben die
            Evangeliumschristen-Baptisten Frankfurt{' '}
            <Link href='https://www.seidheilig.de/informationsschreiben-covid'>
              auf ihrer Website
            </Link>
            . Die Mehrheit hat sich wohl nicht direkt beim Gottesdienst selbst,
            sondern erst im Nachhinein als Folge des Kontakts zu Anwesenden
            angesteckt. Da es in der Kirchgemeinde viele Familien mit fünf und
            mehr Kindern gebe, nehme die Anzahl der Ansteckungen zu Hause weiter
            zu, schreibt die Freikirche auf ihrer Website.
          </p>
          <p>
            In einem{' '}
            <Link href='https://www.cdc.gov/mmwr/volumes/69/wr/mm6920e2.htm'>
              Gottesdienst in Arkansas
            </Link>{' '}
            steckten sich 35 von 92 Teilnehmerinnen und Teilnehmern an, drei von
            ihnen sterben.
          </p>
          <p>
            Mit der Grossveranstaltung einer Freikirche begann im französischen
            Mulhouse die Infektionswelle. An dem Treffen Ende Februar nahmen
            etliche Schweizer teil, die das Virus dann nach Basel und in den
            Jura trugen.
          </p>
          <p>
            In der Schweiz haben die Landeskirchen ihre Schutzkonzepte im
            Hinblick auf die Wiederaufnahme der Gottesdienste angepasst.
            «Aufgrund der hohen Virenverbreitungsgefahr beim Singen muss vorerst
            auf das Singen verzichtet werden», heisst es in den Empfehlungen der
            evangelisch-reformierten Kirche. «Der Gemeindegesang wird
            reduziert», steht dazu in den Bestimmungen des Schutzkonzeptes der
            Schweizer Bischofskonferenz (SBK).
          </p>
          <hr />
          <p>
            LESEN SIE AUCH
            <br />
            <Link
              href='27834193'
              icon='icon file alternate outline'
              allowWordWrap
            >
              Freikirchen-Treffen: Immer mehr Coronafälle in der Schweiz
            </Link>
          </p>
        </div>

        <div id='nachtclub' className='ui text location container'>
          <h3>Bars/Clubs</h3>
          <img
            className='ui fluid image illu'
            src={require('./images/club.svg')}
          />
          <p>
            Über Aprés-Ski-Bars in Ischgl verbreitete sich Sars-CoV-2 Anfang
            März in ganz Europa. An diesen Orten fliesst Alkohol in Strömen, und
            laute Musik macht Gespräche nur aus nächster Nähe möglich.
            Mindestens{' '}
            <Link href='25783010'>27 Schweizerinnen und Schweizer</Link> haben
            sich damals auch infiziert. Die Infizierten fuhren nach den Ferien
            nach Hause ins Berner Oberland, ins Baselbiet oder nach Solothurn.
            Oft bemerkten sie die ersten Symptome der Infektion erst mehrere
            Tage nach ihrer Rückkehr. So konnte sich das Virus aus Ischgl
            ungehindert in der gesamten Deutschschweiz und Europa verbreiten.
          </p>
          <p>
            Auch{' '}
            <Link href='https://www.tdg.ch/lematindimanche/weekend-fete-infecte-verbier/story/12110784'>
              Verbier
            </Link>{' '}
            wurde zu einem Brennpunkt der Corona-Epidemie in der Schweiz. Die
            Zeit, in der das Virus am meisten verbreitet wurde, lässt sich auf
            das Wochenende vom 6. bis 8. März eingrenzen. Am Samstag, 7. März,
            traf die in Verbier lebende Genferin Estelle sechs Freunde im
            Restaurant Le Carrefour. Später gingen alle ins Farinet, eine sehr
            beliebte Disco in Verbier. An diesem Abend seien sie und ihre
            Freunde infiziert worden, vermutet Estelle. Vorerst aber schöpfte
            niemand Verdacht. Die Wochenendgäste gingen am Montag wieder in ihre
            Büros in Genf oder Lausanne – und brachten wohl das Virus mit. In
            Verbier erkrankten zur selben Zeit mehrere Mitarbeiter des Le
            Carrefour. Am 10. März wurde der erste Tourist in Verbier positiv
            getestet, Estelle und ihre Freunde hingegen erst am 16. März. Fast
            zehn Tage lang trugen sie das Virus unbemerkt durch die Westschweiz.
          </p>
          <p>
            In Südkorea hatte ein 29-jähriger infizierter Besucher mehrerer
            Clubs und Bars in dem beliebten Ausgehviertel Itaewon Anfang Mai
            Alarm ausgelöst. Mittlerweile werden mit dem Itaewon-Ausbruch mehr
            als 250 Infektionsfälle in Verbindung gebracht.
          </p>
          <hr />
          <p>
            LESEN SIE AUCH
            <br />
            <Link
              href='25783010'
              icon='icon file alternate outline'
              allowWordWrap
            >
              So kam das Virus in die Schweiz
            </Link>
          </p>
        </div>

        <div id='spital' className='ui text location container'>
          <h3>Spitäler</h3>
          <img
            className='ui fluid image illu'
            src={require('./images/spital.svg')}
          />
          <p>
            Am 9. März wurde ein Patient, der kürzlich in Europa war und
            Symptome von Covid-19 hatte, in die Notaufnahme von St. Augustine's,
            einem privaten Krankenhaus in Durban, Südafrika, eingeliefert. Er
            war zwar nur ein paar Stunden da, doch zurück liess er das Virus.
            Acht Wochen später waren 39 Patienten und 80 Spitalangestellte
            infiziert, 15 Patienten starben. Wissenschaftler der Universität von
            KwaZulu-Natal haben eine{' '}
            <Link href='https://www.sciencemag.org/news/2020/05/study-tells-remarkable-story-about-covid-19-s-deadly-rampage-through-south-african'>
              detaillierte Rekonstruktion
            </Link>{' '}
            der Ausbreitung des Virus veröffentlicht. Das Virus wurde
            hauptsächlich von Mitarbeitern und auf den Oberflächen medizinischer
            Geräte (Thermometern, Blutdruckmanschetten und Stethoskopen) im
            Spital verbreitet. «Es zeigt die Fähigkeit des Virus, sich in einer
            Einrichtung zu verbreiten, wenn keine geeigneten Schutzmassnahmen
            vorhanden sind», sagt Michael Klompas, ein Spezialist für
            Infektionskrankheiten an der Harvard Medical School. Infektionen in
            Spitälern sind keine Seltenheit, wie die grosse Anzahl infizierter
            Beschäftigter im Gesundheitswesen weltweit bestätigt. Das Basler
            Gesundheitsdepartement veröffentlicht Ende April als einer von
            wenigen Kantonen Details zur Ausbreitung des Coronavirus im Kanton
            Basel-Stadt. 19 Prozent hätten sich im familiären Umfeld angesteckt
            und 12 Prozent in Institutionen mit Patientenkontakt wie Spitäler.
            Eine <Link href='547611604066'>grosse Umfrage</Link> der
            «SonntagsZeitung» zeigt hingegen: Bis Mitte Mai gab es nur 2100
            Corona-Fälle bei Ärzten, Pflegepersonal und weiteren Angestellten in
            der Schweiz. Die Umfrage, die 20 Spitäler und Spitalgruppen
            beantwortet haben, deckt fast 100’000 Beschäftigte ab, dies bedeutet
            eine Infektionsrate von etwas mehr als 2 Prozent. Fast alle
            Ansteckungen sind ohne grosse Komplikationen verlaufen, Todesfälle
            gab es keine. Zudem ist anzunehmen, dass sich die meisten positiv
            getesteten Mitarbeiterinnen und Mitarbeiter im Privatleben
            angesteckt haben. Einige Spitäler, zum Beispiel das Unispital
            Zürich, können sogar ausschliessen, dass es bei der Arbeit überhaupt
            eine Ansteckung gegeben hat. Die Krankenhäuser hätten im Gegensatz
            zum Ausland stets ausreichend Schutzmaterial zur Verfügung gehabt.
            Eine Hilfe waren auch die systematischen Tests, wie sie einige
            Spitäler beim Personal durchgeführt haben. So konnten betroffene
            Angestellte sofort isoliert werden.
          </p>
          <hr />
          <p>
            LESEN SIE AUCH
            <br />
            <Link
              href='547611604066'
              icon='icon file alternate outline'
              allowWordWrap
            >
              2100 Covid-Fälle beim Spitalpersonal
            </Link>
          </p>
        </div>

        <div id='fitness' className='ui text location container'>
          <h3>Fitness</h3>
          <img
            className='ui fluid image illu'
            src={require('./images/fitness.svg')}
          />
          <p>
            Auch in Fitnessclubs gab es weltweit viele Ausbrüche. In Südkorea
            waren{' '}
            <Link href='https://wwwnc.cdc.gov/eid/article/26/8/20-0633_article'>
              zwölf
            </Link>{' '}
            Einrichtungen betroffen. Über 100 Leute steckten sich an. Auffällig
            war, dass es hauptsächlich Tanzkurse betraf (60% der Teilnehmer
            steckten sich an), keine Ansteckung erfolgte in Gruppen die Yoga
            oder Pilates praktizierten – obwohl Trainer in beiden Gruppen
            infiziert waren. Das lässt darauf schliessen, dass die Intensität
            des Trainings eine Rolle spielt. Auch die Gruppengrösse ist
            entscheidend, in Kursen mit bis zu fünf Personen gab es keine
            Ansteckungen. «Wir nehmen an, dass die geringere Intensität von
            Pilates und Yoga nicht die gleichen Übertragungseffekte auslöste wie
            die der intensiveren Fitness-Tanzkurse», schreiben{' '}
            <Link href='https://wwwnc.cdc.gov/eid/article/26/8/20-0633_article'>
              Forscher
            </Link>
            . Die Details müssen aber noch genauer untersucht werden.
          </p>
        </div>

        <div id='beerdigung' className='ui text location container'>
          <h3>Beerdigungen, Hochzeiten, Geburtstage, Familienfeste</h3>
          <img
            className='ui fluid image illu'
            src={require('./images/beerdigung.svg')}
          />
          <p>
            Ein grosser Ausbruch bei einem Familienfest wurde in Jordanien{' '}
            <Link href='https://wwwnc.cdc.gov/eid/article/26/9/20-1469_article'>
              dokumentiert
            </Link>
            . 350 Personen nahmen am 13. März an einer Hochzeit teil, 76 wurden
            krank. Der Index-Patient war wohl der Vater der Braut, der vier Tage
            vor der Hochzeit aus Spanien zurückkehrte. Er zeigte schon zwei Tage
            vor dem Fest Symptome wie Husten und Fieber. Die Party fand in einem
            Innenraum statt. Die meisten Infizierten entwickelten nur milde
            Symptome (38), zwei Personen erkrankten schwer. Eine 80-jährige Frau
            starb. Die{' '}
            <Link href='https://wwwnc.cdc.gov/eid/article/26/9/20-1469_article'>
              Studie
            </Link>{' '}
            zeigt einmal mehr das enorme Risko bei Massenversammlungen in
            Innenräumen.
          </p>
          <p>
            Eine andere{' '}
            <Link href='https://www.cdc.gov/mmwr/volumes/69/wr/mm6915e1.htm?s_cid=mm6915e1_w'>
              beschriebene
            </Link>{' '}
            Infektionskette in familiärem Umfeld stammt aus Chicago. Ein
            infizierter Mann nahm an einer Beerdigung teil und umarmte mehrere
            Familienmitglieder. Am Tag darauf besuchte er auch noch eine
            Geburtstagsparty, Gäste dieses Festes gingen anschliessend in die
            Kirche, wo sie sangen. Insgesamt war der Mann Auslöser für
            Infektionen bei 16 Personen im Alter zwischen 5 und 86 Jahren. Drei
            starben. Eine{' '}
            <Link href='https://www.cdc.gov/mmwr/volumes/69/wr/mm6915e1.htm?s_cid=mm6915e1_w'>
              Untersuchung
            </Link>{' '}
            der amerikanischen Seuchenschutzbehörde hält es für möglich, dass
            dieser Cluster für den breiteren Ausbruch in Chicago verantwortlich
            war.
          </p>
          <p>
            Private Feiern mehrerer Grossfamilien zum muslimischen Zuckerfest
            sind jüngst auch für einen Corona-Ausbruch im deutschen Göttingen
            verantwortlich sein. Der Schwerpunkt des Ausbruchs liege in einer
            Wohnanlage. Die Feiern fanden am Wochenende vom 23. und 24 Mai
            statt,{' '}
            <Link href='https://www.welt.de/vermischtes/article208814329/Goettingen-Zahl-der-bestaetigten-Corona-Infektionen-steigt-auf-80.html'>
              inzwischen
            </Link>{' '}
            sind 80 Personen positiv auf das Virus getestet worden. Über 200
            Menschen mussten in häusliche Quarantäne. Weil sich unter den
            Infizierten auch viele Kinder und Jugendliche befinden, erfolgte aus
            Sicherheitsgründen die Schliessung aller Schulen in Göttingen.
          </p>
        </div>

        <div id='schlachthof' className='ui text location container'>
          <h3>Schlachthöfe und Fleischverpackungen</h3>
          <img
            className='ui fluid image illu'
            src={require('./images/schlachthof.svg')}
          />
          <p>
            In fleischverarbeitenden Betrieben müssen Arbeiter mitten von
            ohrenbetäubende Lärm und dicht gedrängt miteinander kommunizieren.
            Zudem herrschen kühle Temperaturen. Der deutsche Virologe Christian
            Drosten stellt die These auf, dass dies die Ausbreitung des Virus
            begünstigen könnte. In den Betrieben herrschten «fast
            Kühlschrank-Temperaturen», und er stelle sich immer mehr die Frage,
            ob die hohen Ansteckungsraten in Schlachthöfen möglicherweise
            zeigen, «was wir im Winter weitflächig erleben werden». Wenn es
            kälter wird, könnte das Virus besser übertragen werden, vermutet
            Drosten. Zuletzt erwischte es einen Schlachthof im niedersächsischen
            Dissen, in dem von 278 Arbeitern 146 positiv getestet wurden. Auch
            der Corona-Ausbruch in einem niederländischen Schlachthof Ende Mai
            erreichte ein grosses Ausmass: Mindestens 150 Beschäftigte
            infizierten sich – 20 Prozent der Belegschaft. In den USA gibt es in
            Hunderten Einrichtungen Ausbrüche, mehr als 5000 Arbeiter sind
            infiziert, es gab mindestens 20 Todesfälle. In Frankreich sind
            mindestens drei Schlachthöfe mit Covid-19-Infektionen in die
            Schlagzeilen geraten.
          </p>
          <hr />
          <p>
            LESEN SIE AUCH
            <br />
            <Link
              href='894643878591'
              icon='icon file alternate outline'
              allowWordWrap
            >
              In deutschen Fleischfabriken grassiert das Virus
            </Link>
          </p>
        </div>

        <div className='ui text container' style={{ margin: '2em 0' }}>
          <p>
            Weitere Orte, an denen es zu gravierenden Ausbrüchen kam, waren
            Pflegeheime, Gefängnisse, religiöse Feiern, Logistikzentren,
            Obdachlosenunterkünfte und Kreuzfahrtschiffe. Eine Datenbank, die
            die Forscherin Gwenan Knight von der London School of Hygiene and
            Tropical Medicine mit Kollegen zusammengestellt hat, listet{' '}
            <Link href='https://wellcomeopenresearch.org/articles/5-83'>
              Hunderte
            </Link>{' '}
            weiterer solcher Ereignisse auf. All diese Beispiele haben eines
            gemeinsam: Immer handelt es sich um lange Zusammentreffen vieler
            Menschen auf engem Innenraum, die viel redeten, sangen oder schrien.
          </p>
          <p>
            Das deckt sich mit aktuellen Untersuchungen, nach denen Aerosole
            eine wichtige Rolle bei der Übertragung des Virus spielen könnten.
            Laut einer{' '}
            <Link href='https://science.sciencemag.org/content/early/2020/05/27/science.abc6197.full'>
              «Science»-Studie
            </Link>{' '}
            können Viruströpfchen beim Niesen und Husten deutlich weiter fliegen
            als gedacht. Die Empfehlung der Weltgesundheitsorganisation (WHO),
            zwei Meter Abstand zu halten, basiere auf Erkenntnissen über die
            Verbreitung der Tröpfchen aus den Dreissigerjahren, schreiben die
            Forscher. Mittlerweile wisse man aber, dass grössere Viruströpfchen
            mehr als sechs Meter weit katapultiert werden könnten. Schon deshalb
            sei das Tragen von Masken wichtig – und das Einhalten von
            ausreichend Abstand erst recht. Das Fazit der Forscher: «Die
            Übertragung von Viren durch Aerosole muss als Schlüsselfaktor für
            die Ausbreitung infektiöser Atemwegserkrankungen anerkannt werden.»
          </p>
          <p>
            Ein kurzer{' '}
            <Link href='https://www.medrxiv.org/content/10.1101/2020.04.04.20053058v1.full.pdf'>
              Einkauf
            </Link>{' '}
            auf dem Markt, der Besuch im Park oder eine vorübergehende Begegnung
            mit einem Jogger birgt hingegen ein geringes Risiko. In einer Studie
            mit mehr als{' '}
            <Link href='https://www.medrxiv.org/content/10.1101/2020.04.04.20053058v1.full.pdf'>
              7300 Fällen in China
            </Link>{' '}
            war nur einer mit der Übertragung im Freien verbunden. Wenn man die
            Distanzregeln im Freien einhält, ist man gut geschützt, die
            Viruslast wird durch die Luft verdünnt und mit dem Wind weggetragen,
            bei schönem Wetter helfen auch Hitze und Sonnenstrahlen, die
            Sars-CoV-2 zusetzen.
          </p>
          <p>
            Die Dauer, in welcher man sich in Innenräumen aufhält, spielt auch
            eine wichtige Rolle. Über lange Zeiträume reichen schon geringe
            Virenmengen aus, die jemand kontiniuierlich ausstösst, um einen
            ganzen Raum zu infizieren. Social Distancing ist draussen sehr
            wirksam, in Räumen unter Umständen nicht wirklich.{' '}
            <Link href='https://www.medrxiv.org/content/10.1101/2020.02.28.20029272v2'>
              Eine Studie aus Japan
            </Link>{' '}
            berechnet ein rund 19-mal höheres Infektionsrisiko in Innenräumen.
          </p>
          <p>
            Die Virologin Muge Cevik von der britischen University of St.
            Andrews schreibt: «Vermeiden Sie engen, anhaltenden Kontakt in
            Innenräumen und im öffentlichen Verkehr». Zudem müssten für einen
            langfristigen Umgang mit dem neuen Virus bessere Lüftungskonzepte
            entwickelt werden für Menschen, die auf engem Raum zusammenleben
            oder -arbeiten. Cevik hat{' '}
            <Link href='https://twitter.com/mugecevik/status/1257392347010215947'>
              Studien
            </Link>{' '}
            aus der ganzen Welt über die Übertragung des Virus gesammelt und
            studiert. Die Studien stammen aus China, Singapur, Taiwa. Die
            Rückverfolgung von Kontakten und das Erkennen von Mustern kann
            helfen, die Übertragungsketten zu stoppen. In der Schweiz wurde
            bisher noch keine systematische Studie zu Ansteckungsorten und der
            Entstehung von Clustern durchgeführt. Die Antwort des BAGs war
            bisher immer die gleiche: «Wir wissen nicht, wo sich die Leute
            angesteckt haben.»
          </p>
          <h4 id='superspreader'>Über die Rolle der Superspreader</h4>
          <p>
            Eine weitere wichtige Erkenntnis, die sich in den letzten Tagen
            verfestigt hat, ist, dass Sars-CoV-2 sich bevorzugt sprunghaft
            verbreitet. Das Virus verbreitet sich hauptsächlich über wenige
            Superspreader, wie auch die Beispiele oben eindrücklich zeigen. Die
            meisten infizierten Menschen stecken niemand anderen an, einer aber
            steckt zehn oder mehr Menschen an. Die gute Nachricht: So ein
            Infektionsgeschehen lässt sich einfacher kontrollieren als eine
            gleichmässige Verteilung. Diese Super-Spreading-Ereignisse,
            ausgelöst meisten durch vorsymptomatische Personen, gilt es in
            erster Linie zu verhindern.{' '}
            <Link href='https://jamanetwork.com/journals/jamainternalmedicine/fullarticle/2765641'>
              Studien
            </Link>{' '}
            zeigen, am infektösesten sind Infizierte direkt vor dem Auftreten
            der Symptome. Forscher der Universität Bern kamen nach der{' '}
            <Link href='https://www.medrxiv.org/content/10.1101/2020.04.25.20079103v2'>
              Sichtung
            </Link>{' '}
            von zahlreichen Wissenschaftsarbeiten zum Schluss, dass 40 bis 60%
            aller Ansteckungen von Personen kommen, die erst nach der Weitergabe
            des Virus Symptome zeigen. Wenn ein Infizierter in diesem
            Zeitfenster in ein Restaurant, eine Bar, einen Fitnessclub oder
            einfach zur Arbeit geht, besteht für seine Umgebung ein hohes
            Infektionsrisiko. Nur zwei Tage später oder früher stellt ein
            Infizierter womöglich kaum noch eine Gefahr in derselben Umgebung
            dar.
          </p>
          <p>
            Um dies mathematisch zu erfassen, ziehen Epidemiologen den
            «Dispersionsparameter» oder «Streufaktor» hinzu, abgekürzt mit k.
            Hat k einen Wert von eins, steckt jeder Infizierte nahezu dieselbe
            Zahl von Menschen an, Superspreader-Ereignisse spielen eine
            untergeordnete Rolle. So ist es bei vielen Influenza-Ausbrüchen.
          </p>
          <p>
            Doch je kleiner k wird, desto mehr Infektionen gehen von wenigen
            Menschen aus. Mit verschiedenen Methoden und unterschiedlichen
            Datensätzen kommen Arbeitsgruppen aus Hongkong, Israel und England
            auf k-Werte zwischen 0,1 und 0,45. Übertragen auf das
            Infektionsgeschehen bedeutet der kleinere Wert, dass etwa 80 Prozent
            der Infektionen durch nur zehn Prozent der Infizierten verursacht
            werden. Beim grösseren Wert würden 20 Prozent der Infizierten 80
            Prozent der Folgeinfektionen auslösen. Die Untersuchung aus Israel
            schliesst sogar nicht aus, dass nur ein Prozent der Infizierten 80
            Prozent der Folgeinfektionen gestartet haben könnte.
          </p>
          <p>
            Die 0,1 stammt aus der Modellrechnung einer Gruppe um den
            Mathematiker Adam Kucharski von der London School of Hygiene &
            Tropical Medicine. Liegt der Wert wirklich in diesem Bereich, könnte
            dies erklären, warum die Pandemie nicht viel schneller um den Globus
            gezogen ist. Nach Kucharskis Berechnungen infizieren 75 Prozent der
            Infizierten gar keine weiteren Personen, etwa sieben Prozent stecken
            jeweils nur einen weiteren Menschen an. Aber drei Prozent der
            Virusträger geben den Erreger an 20 oder mehr Menschen weiter. In
            dem Fall würden die meisten Infektionsketten früh abreissen.
            Sars-CoV-2 müsse im Durchschnitt mindestens viermal in ein bislang
            virusfreies Land importiert werden, um sich dort zu etablieren,
            sagte Kucharski dem Wissenschaftsjournal Science. Gemäss der London
            School of Hygiene & Tropical Medicine kommen
            Superspreading-Ereignisse vor allem in Altersheimen, Kirchen,
            Arbeiterwohnheimen, Krankenhäusern, Fitnesszentren, Bars, Haushalten
            und Schulen vor – also vor allem in geschlossenen Räumen.
          </p>
          <p>
            Trifft es zu, dass sich Sars-CoV-2 im Wesentlichen durch
            Superspreading-Ereignisse ausbreitet, könnte man sich bei der
            Bekämpfung auf diese gefährlichen Situationen konzentrieren. So hat
            es Japan bislang gehalten, wo die Regierung immer wieder vor dem
            Aufenthalt in geschlossenen Räumen, grossen Menschengruppen und
            geringer Distanz zu anderen gewarnt hat, aber weniger restriktiv
            dagegen vorgegangen ist als viele Länder in Europa. Bislang ging die
            Strategie dort auf.
          </p>
          <p>
            Entwarnung für den Besuch bei den Grosseltern gibt es deshalb aber
            nicht. Der kleine k-Wert deutet nur darauf hin, dass das Virus –
            falls man es denn in sich trägt – statistisch gesehen nicht bei
            jedem Besuch springt. Aber es könnte halt doch jedes Mal passieren.
            Viren sind unberechenbar.
          </p>
        </div>
      </div>

      <Footer
        articleId={ARTICLE_ID}
        questions={[
          'Wie informativ war der Beitrag?',
          'Wie gefällt Ihnen die Gestaltung?'
        ]}
        credits={[
          ['Konzept und Umsetzung', 'Marc Brupbacher, Mathias Lutz'],
          [
            'Inspiration',
            'Erin Bromage<br /><a href="https://www.erinbromage.com/post/the-risks-know-them-avoid-them">The Risks - Know Them - Avoid Them</a>'
          ],
          ['Quelle', 'Mit Material und Text von der «Süddeutschen Zeitung»']
        ]}
      >
        <div
          className='ui container'
          style={{ margin: isMobile ? '1.2em 0 3em' : '0 0 3em' }}
        >
          <h4 className='teaser'>Weitere Beiträge zum Thema</h4>
          <ArticleTeasers
            numberOfCards='three'
            articleIds={[
              '594923417757',
              '921118466582',
              '18727947',
              '484628905148'
            ]}
          />
        </div>
      </Footer>
    </div>
  )
}
